import React, { useState } from "react";
import { Card, Button, Typography, Space, Statistic, Row, Col, Tag, Alert, Spin, Breadcrumb, Tabs, Dropdown, MenuProps } from "antd";
import { ArrowLeftOutlined, UserOutlined, ReloadOutlined, BarChartOutlined, TeamOutlined, DashboardOutlined, DownloadOutlined, FileTextOutlined } from "@ant-design/icons";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, useParams } from "react-router-dom";
import { useGetAdminSurveysQuery, useGetSurveyResponsesQuery, useGetSurveyResponseSummaryQuery, useExportAdminSurveyResponsesMutation } from "../../services/nestjs-base-api";
import ResponsesList from "../../components/Survey/ResponsesList";
import ResponseAnalytics from "../../components/Survey/ResponseAnalytics";
import QuestionCoverageDashboard from "../../components/Survey/QuestionCoverageDashboard";

const { Title, Text } = Typography;

export const AdminSurveyResultsPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { surveyHash } = useParams<{ surveyHash: string }>();
    const [activeTab, setActiveTab] = useState('overview');

    // Fetch all surveys to find the one with matching hash
    const { data: surveysData, isLoading: surveysLoading } = useGetAdminSurveysQuery({
        jwtToken: user?.jwtToken!,
        limit: 1000, // Get all surveys to find the one by hash
    }, {
        skip: !user?.jwtToken
    });

    const survey = surveysData?.data?.find(s => s.hash === surveyHash);
    const surveyId = survey?.id;

    const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useGetSurveyResponseSummaryQuery({
        jwtToken: user!.jwtToken,
        surveyId: surveyId!
    }, { skip: !surveyId });

    const { data: responses, isLoading: responsesLoading, refetch: refetchResponses } = useGetSurveyResponsesQuery({
        jwtToken: user!.jwtToken,
        surveyId: surveyId!
    }, { skip: !surveyId });

    const [exportSurveyResponses, { isLoading: isExporting }] = useExportAdminSurveyResponsesMutation();

    const getStatusColor = (status: string): string => {
        switch (status) {
            case 'active': return 'green';
            case 'draft': return 'default';
            case 'paused': return 'orange';
            case 'completed': return 'blue';
            default: return 'default';
        }
    };

    const handleBack = () => {
        navigate('/admin/surveys');
    };

    const handleBackToSurveyDetails = () => {
        navigate(`/admin/surveys/${surveyHash}`);
    };

    const handleViewOrganization = () => {
        navigate(`/admin/organizations`);
    };

    const handleRefresh = async () => {
        try {
            await Promise.all([
                refetchResponses(),
                refetchSummary()
            ]);
        } catch (error) {
            console.error('Refresh failed:', error);
        }
    };

    const handleExport = async (format: 'csv' | 'excel') => {
        if (!surveyId || !survey) return;
        
        try {
            const result = await exportSurveyResponses({
                jwtToken: user!.jwtToken,
                surveyId: surveyId,
                format
            }).unwrap();

            const url = window.URL.createObjectURL(result);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${survey.name.replace(/[^a-zA-Z0-9]/g, '_')}_responses_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    const exportMenuItems: MenuProps['items'] = [
        {
            key: 'csv',
            label: 'Export as CSV',
            icon: <FileTextOutlined />,
            onClick: () => handleExport('csv')
        },
        {
            key: 'excel',
            label: 'Export as Excel',
            icon: <FileTextOutlined />,
            onClick: () => handleExport('excel')
        }
    ];

    if (surveysLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!survey) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Survey Not Found"
                    description="The survey you're looking for doesn't exist or has been removed."
                    type="error"
                    showIcon
                    action={
                        <Button onClick={handleBack}>
                            Back to Surveys
                        </Button>
                    }
                />
            </div>
        );
    }

    const tabItems = [
        {
            key: 'overview',
            label: (
                <span>
                    <BarChartOutlined style={{ marginRight: 8 }} />
                    Overview
                </span>
            ),
            children: (
                <ResponseAnalytics
                    responses={responses || []}
                    summary={summary}
                    loading={responsesLoading || summaryLoading}
                    survey={survey as any}
                />
            )
        },
        {
            key: 'responses',
            label: (
                <span>
                    <TeamOutlined style={{ marginRight: 8 }} />
                    Responses
                </span>
            ),
            children: (
                <ResponsesList
                    responses={responses || []}
                    loading={responsesLoading}
                    survey={survey as any}
                    onResponseDeleted={() => {
                        refetchResponses();
                        refetchSummary();
                    }}
                />
            )
        },
        {
            key: 'coverage',
            label: (
                <span>
                    <DashboardOutlined style={{ marginRight: 8 }} />
                    Question Coverage
                </span>
            ),
            children: (
                <QuestionCoverageDashboard
                    surveyId={survey.id}
                />
            )
        }
    ];

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Breadcrumb */}
            <Breadcrumb style={{ marginBottom: '24px' }}>
                <Breadcrumb.Item>
                    <Button 
                        type="link" 
                        onClick={() => navigate('/admin/dashboard')} 
                        style={{ padding: 0, height: 'auto', lineHeight: 'inherit' }}
                    >
                        Admin
                    </Button>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Button 
                        type="link" 
                        onClick={handleBack} 
                        style={{ padding: 0, height: 'auto', lineHeight: 'inherit' }}
                    >
                        Surveys
                    </Button>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Button 
                        type="link" 
                        onClick={handleBackToSurveyDetails} 
                        style={{ padding: 0, height: 'auto', lineHeight: 'inherit' }}
                    >
                        {survey.name}
                    </Button>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Results</Breadcrumb.Item>
            </Breadcrumb>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={handleBackToSurveyDetails}
                        style={{ marginBottom: '16px' }}
                    >
                        Back to Survey Details
                    </Button>
                    <Title level={2} style={{ margin: 0 }}>
                        Survey Results
                    </Title>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: '16px' }}>
                            {survey.name} • {survey.brandName} • {survey.organisationName}
                        </Text>
                        <Tag color={getStatusColor(survey.status)}>
                            {survey.status.toUpperCase()}
                        </Tag>
                    </div>
                </div>

                <Space>
                    <Button 
                        icon={<UserOutlined />}
                        onClick={handleViewOrganization}
                    >
                        View Organization
                    </Button>
                    <Dropdown 
                        menu={{ items: exportMenuItems }} 
                        placement="bottomRight"
                        disabled={!responses || responses.length === 0}
                    >
                        <Button 
                            icon={<DownloadOutlined />} 
                            loading={isExporting}
                        >
                            Export Data
                        </Button>
                    </Dropdown>
                    <Button 
                        icon={<ReloadOutlined />} 
                        loading={responsesLoading || summaryLoading}
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Space>
            </div>

            {/* Statistics Summary */}
            {summary && (
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Responses"
                                value={summary.totalResponses}
                                suffix="responses"
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Completed Responses"
                                value={summary.completedResponses}
                                suffix="completed"
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Completion Rate"
                                value={summary.completionRate}
                                precision={1}
                                suffix="%"
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Link Accesses"
                                value={summary.totalAccesses || 0}
                                suffix="accesses"
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Admin Alert */}
            <Alert
                type="info"
                showIcon
                message="Admin View"
                description="You are viewing survey results as an administrator. This data belongs to the organization and should be handled according to privacy policies."
                style={{ marginBottom: '24px' }}
            />

            {/* Tabs with Results */}
            <Card>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                />
            </Card>
        </div>
    );
};